import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MteService } from '../Services/mte.service';

export interface AboutVoatzApiEncoded {
  bodyEncoded: string
}

export interface ErrorTypeWithTime {
  error_type: string,
  time?: string
}

export const changeMfaMethodAPI = '/api/mfamethod';
export const verifyOTPAPI = '/api/verifyotp'; // Note: this is only to change MFA method.
export const changePasswordAPI = '/api/changepassword';
export const getResetPwTokenAPI = '/api/getresetpwtoken';
export const resetPasswordAPI = '/api/resetpassword';
export const updateDashboardAPI = '/api/update_dashboard';
export const idUploadAPI = '/api/document/id';
export const headshotUploadAPI = '/api/document/headshot';
export const submitOrgKeyAPI = '/api/org_key';
export const verifyWithOrgAPI = '/api/org_verify';
export const getPollResultsAPI = '/api/choice_counts';

class BaseAPIService {
  mteService: MteService;
  constructor(private http: HttpClient, mteService: MteService) {
    this.mteService = mteService;
  }

  post<T>(url: string, body: object | string, headers?: HttpHeaders): Observable<HttpResponse<T>> {
    let mkePayload: string | null;

    if(headers){
      if(headers.get('content-type') !== null) {
        headers = headers.append('x-file-type', headers.get('content-type')!)
          .set('content-type', "text/plain");
      }
      headers = headers.append("x-client-id", this.mteService.mteInitialization.personalization_string);
    } else {
      headers = new HttpHeaders({ 
        'content-type': "text/plain", 
        "x-client-id": this.mteService.mteInitialization.personalization_string, 
      });
    }

    if(body instanceof Object) {
      mkePayload = this.mteService.mkeEncryptString(JSON.stringify(body));      
    } else {
      mkePayload = this.mteService.mkeEncryptString(body);
    }

    return this.http.post<string>(
      url,
      mkePayload,
      {
        headers,
        observe: 'response' as 'response',
        withCredentials: true,
      },
    ).pipe(map(res => {
        let decryptedResponse: string | null = "";
        if(res.body) {
          if(res.headers.get("x-client-id")){
            decryptedResponse = this.mteService.mkeDecryptB64(res.body);
            if(decryptedResponse) {
              return new HttpResponse<T>({
                body: JSON.parse(decryptedResponse), 
                headers: res.headers, 
                status: res.status,
                statusText: res.statusText,
                url: res.url ? res.url : undefined
              });
            }
            else {
              this.mteService.needHandshake = true;
              throw new Error("HTTP response decryption error.");
            }
          }
          else {
            // The backend lost the encryption and decryption states. 
            // Perform the handshake again.
            // Return the plaintext response.
            this.mteService.needHandshake = true;
            return new HttpResponse<T>({
              body: JSON.parse(res.body), 
              headers: res.headers, 
              status: res.status,
              statusText: res.statusText,
              url: res.url ? res.url : undefined
            });
          }
        }
        else {
          return new HttpResponse<T>({
            headers: res.headers, 
            status: res.status,
            statusText: res.statusText,
            url: res.url ? res.url : undefined});
        }
      }),
      catchError(
        e => {
          throw e;
        }
      )
    );
  }

  postWithHandshakeCheck<T>(url: string, body: object | string, headers?: HttpHeaders): Observable<HttpResponse<T>> {
    let response: Observable<HttpResponse<T>>;
    // Check if a handshake is needed.
    // If so, make the handshake, and when it succeeds make the request.
    // If the handshake fails, give it another try before throwing an error.
    if (this.mteService.needHandshake) {
      return from(this.mteService.handshake()).pipe(switchMap((succeeded) => {
        if (succeeded) {
          return this.post<T>(url, body, headers).pipe(switchMap( (response) => {
            // If the backend loses the MTE state/keys, give it another attempt.
            if (this.mteService.needHandshake) {
              return from(this.mteService.handshake()).pipe(switchMap((success) => {
                if (success) {
                  return this.post<T>(url, body, headers);
                } else {
                  throw new Error("Handshake failed.");
                }
              }));
            }
            else {
              return of(response);
            }
          } ))
        } else {
          return from(this.mteService.handshake()).pipe(switchMap((success) => {
            if (success) {
              return this.post<T>(url, body, headers);
            } else {
              throw new Error("Handhsake failed.");
            }
          }))
        }
      }))
    } else {
      // If Handshake is not initially needed, make the request.
      // If handshake is needed after the request is observed, we need to try a handshake.
      // If the handshake succeeds, try the request again, otherwise throw error.
      return this.post<T>(url, body, headers).pipe(switchMap((response) => {
        if (this.mteService.needHandshake) {
          return from(this.mteService.handshake()).pipe(switchMap((succeeded) => {
            if (succeeded) {
              return this.post<T>(url, body, headers);
            } else {
              throw new Error("Handshake failed.");
            }
          }))
        } else {
          // This line is the typical, normal behavior of this function.
          return of(response);
        }
      }), catchError( (e) => {
        if (this.mteService.needHandshake) {
          return from(this.mteService.handshake()).pipe(switchMap((succeeded) => {
            if (succeeded) {
              return this.post<T>(url, body, headers);
            } else {
              throw new Error("Handshake failed.");
            }
          }))
        } else {          
          if ((e.error_type !== undefined && (e.error_type === 'DECODER_STATE_LOST' || e.error_type === 'INVALID_MTE_DECODING' )) ||
          (e.error !== undefined && (e.error.error_type === 'DECODER_STATE_LOST' || e.error.error_type === 'INVALID_MTE_DECODING' ))){
            return from(this.mteService.handshake()).pipe(switchMap((succeeded) => {
              if (succeeded) {
                return this.post<T>(url, body, headers);
              } else {
                throw new Error("Handshake failed.");
              }
            }))
          } else {
            throw e;
          }
        }
      }))
    }
  }

  get<T>(url: string): Observable<T> {
    return this.http.get<string>(
      url,
      {
        headers: new HttpHeaders({ 
          'Content-Type': "text/plain", 
          "x-client-id": this.mteService.mteInitialization.personalization_string, 
        }),
        observe: 'body',
        withCredentials: true,
      },
    ).pipe(map(res => {
      let decryptedResponse: string | null = "";
      if(res) {
        decryptedResponse = this.mteService.mkeDecryptB64(res);
        if(decryptedResponse) {
          return JSON.parse(decryptedResponse);
        } 
        else {
          throw new Error("HTTP response decryption error.");
        }
      }
    }));
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApiService extends BaseAPIService {

  constructor(http: HttpClient, mteService: MteService) {
    super(http, mteService);
  }

}

